import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '../Icon';
import { Wordmark } from '../../brand';
import { cn } from '../../lib/cn';
import { isNavItemActive } from './navModel';
import { initialsFor } from './UserMenu';
import { useAuth } from '../../../hooks/useAuth';
import { routes } from '../../../routes/routes';

/**
 * ConsoleShell sidebar, desktop only. 260px, persistent, grouped.
 *
 * The active item is a background pill plus a 3px accent rail on its left edge,
 * both already in `.dsk-sidebar__item.is-active` and its `::before`. The rail
 * is ACCENT because "this is where you are" is state. The item's icon switches
 * to Phosphor's fill weight for the same reason.
 *
 * The admin routes are a VARIANT of this, never a fork: the same component with
 * different groups and an identity badge on the brand line.
 *
 * @param {{
 *   groups: Array<{ id: string, label?: string, items: Array<object> }>,
 *   badge?: string,
 *   brandTo?: string,
 *   userLinkTo?: string,
 *   userLinkLabel?: string,
 *   label?: string,
 *   className?: string
 * }} props
 */
export function Sidebar({
  groups = [],
  badge,
  /* Home, like every other lockup in the app — see `brand/index.jsx`. From
     the admin console that reads as "leave the console", which is what a
     brand click has always meant here; it just lands on the front door now
     rather than on one tab of it. */
  brandTo = routes.HOME,
  userLinkTo = routes.PROFILE,
  userLinkLabel = 'View profile',
  label = 'Console',
  className,
}) {
  const { pathname } = useLocation();
  const { user, isAuthenticated } = useAuth();

  return (
    <aside className={cn('dsk-sidebar', className)}>
      {/* The badge sits OUTSIDE `.dsk-sidebar__brand`, because that rule tints
          every span it contains with the accent to paint the brand tick, and a
          pill caught by it would come out accent coloured. That tick no longer
          exists now the real lockup is here, but the badge stays outside: the
          brand is a link and the badge is not part of what it links to. */}
      <div className="dsk-sidebar__brandrow">
        <Wordmark className="dsk-sidebar__brand" to={brandTo} />
        {badge && <span className="dsk-pill dsk-pill--ink">{badge}</span>}
      </div>

      <nav className="dsk-sidebar__nav" aria-label={label}>
        {groups.map((group) => (
          <div className="dsk-sidebar__group" key={group.id}>
            {group.label && <div className="dsk-sidebar__label">{group.label}</div>}
            {group.items.map((item) => {
              const active = isNavItemActive(item, pathname);
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  className={cn('dsk-sidebar__item', active && 'is-active')}
                  aria-current={active ? 'page' : undefined}
                >
                  {item.icon && <Icon as={item.icon} active={active} />}
                  {item.label}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {isAuthenticated && (
        <div className="dsk-sidebar__user">
          <span className="dsk-avatar" aria-hidden="true">
            {initialsFor(user)}
          </span>
          <div>
            <div className="dsk-sidebar__username">
              {user?.full_name || user?.name || 'Your account'}
            </div>
            <Link className="dsk-sidebar__userlink" to={userLinkTo}>
              {userLinkLabel}
            </Link>
          </div>
        </div>
      )}
    </aside>
  );
}

export default Sidebar;
